import { HttpClient, HttpParams } from '@angular/common/http';
import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { BehaviorSubject, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BaseApiService } from './baseApi.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { decodeJwtPayload } from '@app/_helpers/functions/jwt-functions';
import { toLanguageCode } from '@app/_helpers/functions/language-functions';
import { CommandResultWithValue } from '@app/_models/command/commandResultWithValue';
import { PermissionObject } from '@app/_models/enums/permissionObject';
import { ObjectPermissionQueryResultDto } from '@app/_models/objectPermissionQueryResultDto';
import { ObjectPermissionsQueryDto } from '@app/_models/objectPermissionsQueryDto';
import { UserService } from '@app/_services/user.service';
import * as Sentry from '@sentry/angular';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root',
})
export class AccountService extends BaseApiService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService);
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(BsLocaleService);
  private readonly errorHandler = inject(ErrorHandler);

  private readonly currentJwt = new BehaviorSubject<DecodedToken | undefined>(undefined);
  private readonly _authInitialized$ = new ReplaySubject<true>(1);
  readonly authInitialized$ = this._authInitialized$.pipe(take(1));

  hasOtpConfigured$ = new BehaviorSubject<boolean>(false);
  currentJwt$ = this.currentJwt.asObservable();
  currentUser$ = this.currentJwt.pipe(map((it) => it?.preferred_username));
  private readonly currentUserRolesSource = new ReplaySubject<string[] | undefined>(1);
  currentUserRoles$ = this.currentUserRolesSource.asObservable();
  userRoles?: string[];

  constructor() {
    super('account');
    this.currentJwt$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((token) => {
      this.check2FAStatus(token);
      Sentry.setUser(
        token
          ? {
              id: token.sub,
            }
          : null,
      );
    });

    // Propagate token updates from the OAuth library regardless of which component is mounted.
    // Without this, currentUser$ would not fire when the auth code is exchanged after a Keycloak
    // redirect lands on a route other than /account/login.
    this.oauthService.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === 'token_received' && this.oauthService.hasValidAccessToken()) {
        this.setUsernameFromToken();
        this.updateUserRoles();
        this.loadUserLanguage();
      }
    });
  }

  register(model: unknown): Observable<CommandResultWithValue<RegisterAccountResult>> {
    return this.http.post<CommandResultWithValue<RegisterAccountResult>>(this.baseControllerUrl + '/register', model);
  }

  queryObjectPermissions(query: ObjectPermissionsQueryDto[]): Observable<ObjectPermissionQueryResultDto[]> {
    return this.http.post<ObjectPermissionQueryResultDto[]>(this.baseControllerUrl + '/objectpermissions/query', query);
  }

  getObjectPermission(
    permissionObject: PermissionObject,
    objectId: number,
  ): Observable<ObjectPermissionQueryResultDto> {
    const params = new HttpParams().set('permissionObject', permissionObject).set('objectId', objectId);

    return this.http.get<ObjectPermissionQueryResultDto>(this.baseControllerUrl + '/objectpermission', { params });
  }

  markAuthInitialized() {
    this._authInitialized$.next(true);
  }

  setUsernameFromToken() {
    if (this.oauthService.hasValidAccessToken()) {
      const decodedToken = decodeJwtPayload<DecodedToken>(this.oauthService.getAccessToken());
      this.currentJwt.next(decodedToken ?? undefined);
    } else {
      this.logout();
    }
  }

  private readonly _postLoginRedirectPath = 'post_login_redirect_path';

  updateUserRoles() {
    if (this.oauthService.hasValidAccessToken()) {
      this.userService
        .getUserRoles()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (roles) => {
            this.userRoles = roles;
            this.currentUserRolesSource.next(this.userRoles);
            this.hasRole('UserRegistrationPending')
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((isRegistrationPending) => {
                if (isRegistrationPending) {
                  this.router.navigate(['/account/register']);
                } else if (this.router.url.includes('/account/login')) {
                  const postLoginPath = sessionStorage.getItem(this._postLoginRedirectPath);
                  if (postLoginPath) {
                    sessionStorage.removeItem(this._postLoginRedirectPath);
                    this.router.navigateByUrl(postLoginPath);
                  } else {
                    this.router.navigate(['/home/dashboard']);
                  }
                }
              });
          },
          error: (error: unknown) => {
            // Report like an unhandled error
            this.errorHandler.handleError(error);
            this.currentUserRolesSource.next([]);
          },
        });
    } else {
      this.logout();
    }
  }

  async linkIdpAndForceLogin(identityProvider: string, redirectPath = '/home/dashboard') {
    const urlTree = this.router.createUrlTree(['/account/force-login-with-idp'], {
      queryParams: { idp: identityProvider, postLoginRedirectPath: redirectPath },
    });
    await this.navigateToLinkingIdp(identityProvider, this.router.serializeUrl(urlTree));
  }

  async navigateToLinkingIdp(idp: string, redirectPath: string) {
    // Use Keycloak's Application Initiated Action (AIA) for account linking
    // The old /broker/{idp}/link endpoint is deprecated and broken in Keycloak 26+
    this.executeKcAction(`idp_link:${idp}`, redirectPath);
  }
  logout() {
    // remove old localstorage entries from users that used the old login with .net identity
    localStorage.removeItem('loginToken');
    localStorage.removeItem('loginTokenTimestamp');

    // trigger logout flow only if we have a JWT currently, a loop may be triggered otherwise
    if (this.currentJwt.getValue()) {
      this.currentJwt.next(undefined);
      this.currentUserRolesSource.next(undefined);
      // pass the client id always to mitigate problems if the
      // id token wasn't stored (https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1443)
      this.oauthService.revokeTokenAndLogout({ client_id: this.oauthService.clientId });

      if (this.router.url != '/account/login') {
        this.router.navigate(['/account/login']);
      }
    }
  }

  /**
   * Whether the current user is an InstitutionGroupAdmin (Klinikgruppenadmin) but not a full Admin.
   * Full Admins also carry the InstitutionGroupAdmin role but should not be restricted by it.
   */
  get isInstitutionGroupAdminOnly(): boolean {
    return !(this.userRoles ?? []).includes('Admin') && (this.userRoles ?? []).includes('InstitutionGroupAdmin');
  }

  hasRole(roleToCheck: string): Observable<boolean> {
    return this.currentUserRoles$.pipe(
      map((roles) => {
        if (roles) {
          return roles.includes(roleToCheck);
        }

        return false;
      }),
    );
  }

  hasAllRoles(rolesToCheck: string[]): Observable<boolean> {
    return this.currentUserRoles$.pipe(
      map((roles) => {
        if (roles) {
          return rolesToCheck.every((roleName) => roles.includes(roleName));
        }

        return false;
      }),
    );
  }

  hasAnyRole(rolesToCheck: string[]): Observable<boolean> {
    return this.currentUserRoles$.pipe(
      map((roles) => {
        if (roles) {
          return rolesToCheck.findIndex((roleName) => roles.includes(roleName)) > -1;
        }

        return false;
      }),
    );
  }

  loadUserLanguage() {
    /*
     After log in, load the language explicitly set on the profile.
     The case where the browser language is different from the profile language is probably rare,
     but we don't want to overwrite the profile language with our default choice of browser language if
     the user has never explicitly changed the language.
    */
    this.userService
      .getUserProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.language) {
          this.setUserLanguage(result.language);
        }
      });
  }

  setUserLanguage(language: string) {
    this.translate.use(language);
    this.localeService.use(language);
    localStorage.setItem('userLanguage', language);
  }

  getUserLanguage(): string {
    return (localStorage.getItem('userLanguage') || this.translate.getFallbackLang()) ?? '';
  }

  loginWithIdp(idp: string) {
    this.oauthService.initImplicitFlow(this.getReturnUrl(), {
      kc_idp_hint: idp,
      prompt: 'login',
    });
  }

  async forceLoginWithIdp(idp: string, postLoginRedirectPath: string) {
    sessionStorage.setItem(this._postLoginRedirectPath, postLoginRedirectPath);
    this.oauthService.logOut(true); // do not redirect to logout, just clear session
    this.loginWithIdp(idp);
  }

  changePassword() {
    this.executeKcAction('UPDATE_PASSWORD');
  }

  configureOtp() {
    this.executeKcAction('CONFIGURE_TOTP');
  }

  initLoginFlow() {
    this.oauthService.initLoginFlow(this.getReturnUrl(), {
      ui_locales: toLanguageCode(this.getUserLanguage()),
      prompt: 'login',
    });
  }

  /**
   * `?returnUrl` from the login page (set by the AuthGuard), passed to Keycloak as OAuth `state`.
   * The pending navigation is checked first because the LoginRedirectGuard runs mid-navigation.
   */
  private getReturnUrl(): string {
    const queryParams =
      this.router.currentNavigation()?.extractedUrl.queryParams ?? this.router.routerState.snapshot.root.queryParams;
    return queryParams.returnUrl ?? '';
  }

  navigateToReturnUrlAfterLogin() {
    const returnUrl = this.oauthService.state;
    if (returnUrl) {
      this.router.navigateByUrl(decodeURIComponent(returnUrl), { replaceUrl: true });
    }
  }

  executeKcAction(kcAction: string, postLoginRedirectPath: string | undefined = undefined) {
    const searchParams = new URLSearchParams({
      client_id: this.oauthService.clientId ?? '',
      redirect_uri: this.oauthService.redirectUri + (postLoginRedirectPath ?? ''),
      response_type: 'code',
      scope: 'openid',
      kc_action: kcAction,
      ui_locales: toLanguageCode(this.getUserLanguage()),
    });

    globalThis.location.href = `${this.oauthService.issuer}/protocol/openid-connect/auth?${searchParams}`;
  }

  loadRolesAndLanguages() {
    if (this.oauthService.hasValidAccessToken()) {
      this.setUsernameFromToken();
      this.updateUserRoles();
      this.loadUserLanguage();
    }
  }

  async getUserCredentials(): Promise<UserCredential[] | undefined> {
    const iss = this.currentJwt.getValue()?.iss;

    if (!iss) {
      return;
    }

    const keycloakAccountUrl = `${iss}/account/credentials`;

    try {
      return await firstValueFrom(
        this.http.get<UserCredential[]>(keycloakAccountUrl, {
          headers: {
            Authorization: `Bearer ${this.oauthService.getAccessToken()}`,
          },
        }),
      );
    } catch {
      // Best-effort check: the request can fail with 401 or get cancelled during logout.
      // check2FAStatus runs fire-and-forget, so don't let the rejection bubble up.
      return undefined;
    }
  }

  async check2FAStatus(jwt: DecodedToken | undefined) {
    if (!jwt) {
      return;
    }

    /**
     * This works if the token contains the 'account' audience, which has to be
     * configured in Keycloak
     */
    if (jwt?.aud != 'account') {
      return;
    }

    if (jwt?.identity_provider) {
      // no need to check for 2fa if we have an identity provider
      return;
    }

    const userCredentials = await this.getUserCredentials();

    if (userCredentials) {
      this.hasOtpConfigured$.next(
        userCredentials.some((cred) => cred.type === 'otp' && (cred.userCredentialMetadatas?.length ?? 0) > 0),
      );
    }
  }
}

/** Query parameter key for login option */
export const LOGIN_OPTION_PARAM = 'loginOption';

/** Login option for email/password authentication */
export const EMAIL_PASSWORD_OPTION = 'EmailPassword';

export interface DecodedToken {
  sub: string;
  aud?: string;
  iss: string;
  preferred_username: string;
  identity_provider: string | undefined;
  session_state: string;
  given_name: string | undefined;
  family_name: string | undefined;
  email: string | undefined;
}

interface RegisterAccountResult {
  userId: string;
  lockoutEnabled: boolean;
}

interface UserCredential {
  type: string;
  userCredentialMetadatas?: unknown[];
}
